import React, { useState, useEffect, useCallback } from 'react';
import { DataPacket_Kind } from 'livekit-client';
import { useDispatch, useSelector } from 'react-redux';
// 👇 ASSUREZ-VOUS D'IMPORTER VOTRE ACTION 'updateGameState' ICI
import { removeActiveGame, updateGameState } from '../../store/GamesSlice';
import './TicTacToe.css';
import congratsGif from "../../../../assets/congratulations.gif";
import { useTranslation } from 'react-i18next';

const styles = {
  board: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', maxWidth: '21.875rem', margin: '0 auto' },
  cell: { aspectRatio: '1/1', background: '#f1f5f9', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', fontWeight: 'bold', cursor: 'pointer', border: '2px solid transparent', transition: 'all 0.2s', color: '#334155' },
  cellActive: { background: '#e2e8f0', cursor: 'pointer' },
  cellDisabled: { cursor: 'default', opacity: 0.8 },
  status: { },
  turnIndicator: { fontWeight: 'bold', color: '#2563eb' }
};

export default function TicTacToe({
  room,
  gameId,
  player1Id,
  player2Id,
  player1Name,
  player2Name,
  isSpectator
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // ------------------------------------------------------------
  // 1. INITIALISATION DEPUIS REDUX (CRUCIAL POUR SPECTATEURS)
  // ------------------------------------------------------------
  const currentGameFromStore = useSelector(state =>
    state.games.activeGames.find(g => g.gameId === gameId)
  );

  // Si Redux contient déjà un plateau (reçu via SYNC), on l'utilise. Sinon vide.
  const [board, setBoard] = useState(currentGameFromStore?.board || Array(9).fill(null));

  // Idem pour le tour et le gagnant
  const [isXNext, setIsXNext] = useState(
    currentGameFromStore?.isXNext !== undefined ? currentGameFromStore.isXNext : true
  );
  const [winner, setWinner] = useState(currentGameFromStore?.winner || null);

  const myId = room.localParticipant.identity;
  const amIX = (myId === player1Id);
  const amIO = (myId === player2Id);
  const isMyTurn = !isSpectator && !winner && ((isXNext && amIX) || (!isXNext && amIO));

  // --- HELPER VICTOIRE ---
  const checkWinner = useCallback((currentBoard) => {
    const lines = [[0, 1, 2], [3, 4, 5], [6, 7, 8], [0, 3, 6], [1, 4, 7], [2, 5, 8], [0, 4, 8], [2, 4, 6]];
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (currentBoard[a] && currentBoard[a] === currentBoard[b] && currentBoard[a] === currentBoard[c]) {
        return currentBoard[a];
      }
    }
    if (!currentBoard.includes(null)) return 'Draw';
    return null;
  }, []);

  // ------------------------------------------------------------
  // 2. GESTION DES COUPS REÇUS (ADVERSAIRE)
  // ------------------------------------------------------------
  useEffect(() => {
    const onDataReceived = (payload, participant) => {
      const decoder = new TextDecoder();
      const data = JSON.parse(decoder.decode(payload));

      if (data.gameId !== gameId) return;

      if (data.type === 'GAME_MOVE') {
        setBoard(prev => {
          const newBoard = [...prev];
          newBoard[data.index] = data.symbol;

          const win = checkWinner(newBoard);
          if (win) setWinner(win);

          // 🔥 SAUVEGARDE DANS REDUX (Pour que MediaDisplay soit à jour)
          dispatch(updateGameState({
            gameId,
            board: newBoard,
            isXNext: data.nextTurn === 'X',
            winner: win
          }));

          return newBoard;
        });
        setIsXNext(data.nextTurn === 'X');
      }

      if (data.type === 'GAME_RESET') {
        setBoard(Array(9).fill(null));
        setIsXNext(true);
        setWinner(null);

        // 🔥 RESET DANS REDUX
        dispatch(updateGameState({
          gameId,
          board: Array(9).fill(null),
          isXNext: true,
          winner: null
        }));
      }
    };

    room.on('dataReceived', onDataReceived);
    return () => room.off('dataReceived', onDataReceived);
  }, [room, gameId, checkWinner, dispatch]);

  // ------------------------------------------------------------
  // 3. GESTION DE MON COUP (LOCAL)
  // ------------------------------------------------------------
  const handleCellClick = async (index) => {
    if (board[index] || isSpectator || !isMyTurn || winner) return;

    const symbol = isXNext ? 'X' : 'O';
    const nextTurnSymbol = isXNext ? 'O' : 'X';
    const nextTurnBool = !isXNext; // Pour Redux

    const newBoard = [...board];
    newBoard[index] = symbol;

    // A. Mise à jour UI Immédiate
    setBoard(newBoard);
    setIsXNext(nextTurnBool);

    const win = checkWinner(newBoard);
    if (win) setWinner(win);

    // B. Mise à jour REDUX (Indispensable pour la synchro future)
    dispatch(updateGameState({
      gameId: gameId,
      board: newBoard,
      isXNext: nextTurnBool,
      winner: win
    }));

    // C. Envoi LiveKit
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify({
      type: 'GAME_MOVE',
      gameId: gameId,
      index: index,
      symbol: symbol,
      nextTurn: nextTurnSymbol
    }));

    try {
      await room.localParticipant.publishData(data, {
        reliability: DataPacket_Kind.RELIABLE,
        destinationIdentities: []
      });
    } catch (error) { console.error("Erreur envoi coup:", error); }
  };

  const handleReset = async () => {
    if (isSpectator) return;

    // Reset Local
    setBoard(Array(9).fill(null));
    setIsXNext(true);
    setWinner(null);

    // Reset Redux
    dispatch(updateGameState({
      gameId,
      board: Array(9).fill(null),
      isXNext: true,
      winner: null
    }));

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify({
      type: 'GAME_RESET',
      gameId: gameId
    }));

    await room.localParticipant.publishData(data, {
      reliability: DataPacket_Kind.RELIABLE,
      destinationIdentities: []
    });
  };

  // --- RENDU (Identique à avant) ---
  let statusMessage;
  if (winner === 'Draw') {
    statusMessage = t("draw");
  } else if (winner) {
    const winnerName =
    winner === 'X' ? player1Name : player2Name;
    statusMessage = t("win", { user: winnerName });  
  } else {
    if (isMyTurn) {
      statusMessage = (<span style={{ color: '#16a34a', fontWeight: 'bold' }}>
      
        {t("your_move", { user: isXNext ? "X" : "O" })}
      
      </span>);
    } else {
      const currentPlayerName = isXNext ? player1Name : player2Name;

        statusMessage = t("his_move", {
          user: currentPlayerName,
          symbol: isXNext ? "X" : "O"
        });
      }
  }

  const isWinnerMe =
    (winner === 'X' && amIX) ||
    (winner === 'O' && amIO);

  return (
    <div className='game-body-xo' style={{ width: '100%', height: '100%' }}>
      <div className='game-grid' style={styles.board}>
        {board.map((cell, i) => (
          <div
            className='grid-game-item'
            key={i}
            onClick={() => handleCellClick(i)}
            style={{
              ...styles.cell,
              ...((!cell && isMyTurn && !winner) ? styles.cellActive : styles.cellDisabled),
              color: cell === 'X' ? '#ef4444' : '#3b82f6'
            }}
          >
            <span>{cell}</span>
          </div>
        ))}
      </div>

      <div className='game-turn'>{statusMessage}</div>

      {winner && !isSpectator && (
        <div className='result-game'>
          {isWinnerMe ?
            <>
              <div class="congrats-content">
                <h2>{t("congratulations")} </h2>
                <p>{t("you_won")}</p>
              </div>
              <img
                src={congratsGif}
                alt="Victory"
                className="win-animation"
              />
            </> :
            <>
              <div class="lose-popup" id="losePopup">
                <div class="lose-content">
                  <h2>{t("oops")} </h2>
                  <p>{t("you_lost")} </p>
                </div>
              </div>
            </>
          }

          <div style={{ textAlign: 'center', marginTop: '0.9375rem' }}>
            <button onClick={handleReset} style={{ padding: '0.5rem 1rem', background: '#0f172a', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}>
              {t("replay")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}